// Shared by `server/middleware/maintenance.ts` and `app/middleware/maintenance.global.ts`. Paths are passed
// through `stripLocalePrefix` first.

export const MAINTENANCE_PAGE = '/maintenance'

// Kept reachable in every mode, so an admin can still sign in and switch maintenance off again.
const MAINTENANCE_ALWAYS_ALLOWED_API = [
    '/api/_auth',
    '/api/auth/user/log-in',
    '/api/auth/user/session',
    '/api/auth/logout',
    '/api/auth/slack/login',
    '/api/auth/slack/callback',
    '/api/users/get-self',
    '/api/maintenance',
]

// Writes that don't change content, so they stay allowed while the CMS is read-only.
const MAINTENANCE_SOFT_ALLOWED_WRITES = [
    '/api/activity/seen',
]

const MAINTENANCE_HARD_ALLOWED_PAGES = [
    MAINTENANCE_PAGE,
    '/login',
    '/__nuxt_error',
]

const READ_METHODS = ['GET', 'HEAD', 'OPTIONS']

export function isMaintenanceApiAllowed(mode: MaintenanceMode, path: string, method: string): boolean {
    if (mode === 'disabled') return true
    if (matchesRoute(path, MAINTENANCE_ALWAYS_ALLOWED_API)) return true
    if (mode === 'hard') return false

    if (READ_METHODS.includes(method.toUpperCase())) return true
    return matchesRoute(path, MAINTENANCE_SOFT_ALLOWED_WRITES)
            || /^\/api\/bucketContent\/[^/]+\/validate-unique$/.test(path)
}

export function isMaintenancePageAllowed(mode: MaintenanceMode, path: string): boolean {
    return mode !== 'hard' || matchesRoute(path, MAINTENANCE_HARD_ALLOWED_PAGES)
}

// Keeps the locale prefix of the page being left, e.g. `/nl/dashboard` -> `/nl/maintenance`.
export function maintenancePagePath(path: string): string {
    const prefix = stripLocalePrefix(path) === path ? '' : `/${ path.split('/')[1] }`
    return `${ prefix }${ MAINTENANCE_PAGE }`
}

// Dispatched on `window` by requests that don't go through `$fetch` (XHR uploads) when maintenance rejected them.
export const MAINTENANCE_REJECTED_EVENT = 'cms:maintenance-rejected'

// Matches the 503 that `server/middleware/maintenance.ts` throws, which carries the mode in its `data`.
export function isMaintenanceRejection(status: number, body: unknown): boolean {
    return status === 503 && !!(body as { data?: { maintenance?: unknown } } | undefined)?.data?.maintenance
}
